import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { DTA_SEARCH_PAGE_SIZE, DtaAccountField, fiduciarieMessages, PaginationResponse } from 'azshared';
import type { DtaListedAccount, InfoDiPaginazione } from 'azshared';
import { Fragment, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { PageSelector } from '../../customer-service/results/page-selector/page-selector';
import { FiduciarieResultsColumn, FiduciarieResultsRow } from './fiduciarie-results-row';
import './fiduciarie-results-table.scss';

interface FiduciarieResultsTableProps {
  accounts: DtaListedAccount[];
  pagination?: InfoDiPaginazione;
  showPage: (page: number) => void;
  showPreviousPage: () => void;
  showNextPage: () => void;
  onDelete?: (account: DtaListedAccount) => void;
  onOpen?: (account: DtaListedAccount) => void;
}

// The main fields of DtaListedAccount: the detail page shows all of them.
const columns: FiduciarieResultsColumn[] = [
  { key: 'username', label: fiduciarieMessages.labelUsername, value: (account) => account.username },
  {
    key: 'fullName',
    label: fiduciarieMessages.labelNominativoFiduciante,
    value: (account) => `${account.actingPerson.name} ${account.actingPerson.surname}`
  },
  { key: DtaAccountField.TrustCompanyCode, label: fiduciarieMessages.labelCodiceClienteRappresentante, value: (account) => account.trustCompany.code },
  { key: DtaAccountField.AddressBook, label: fiduciarieMessages.labelCodiceRubrica, value: (account) => account.addressBook },
  {
    key: DtaAccountField.TrustCompanyName,
    label: fiduciarieMessages.labelRagioneSocialeFiduciaria,
    value: (account) => account.trustCompany.name
  },
  { key: DtaAccountField.ContactPhone, label: fiduciarieMessages.labelNumeroCellulare, value: (account) => account.contact.phone },
  { key: DtaAccountField.ContactEmail, label: fiduciarieMessages.labelEmail, value: (account) => account.contact.email }
];

export const FiduciarieResultsTable = (props: FiduciarieResultsTableProps) => {
  const { accounts, pagination, showPage, showPreviousPage, showNextPage, onDelete, onOpen } = props;
  const { formatMessage } = useIntl();

  // PageSelector works on a total count, while the API returns the total number of pages.
  const pageSelectorPagination: PaginationResponse = useMemo(() => {
    const perPage = pagination?.elementiPerPagina || DTA_SEARCH_PAGE_SIZE;
    return {
      page: pagination?.paginaCorrente ?? 1,
      perPage,
      count: (pagination?.pagineTotali ?? 0) * perPage
    };
  }, [pagination]);

  return (
    <Fragment>
      <TableContainer
        className='results-table fiduciarie-results-table'
        sx={{
          maxHeight: '400px',
          overflowY: 'auto',
          overflowX: 'auto',
          width: '100%'
        }}>
        <Table aria-label='fiduciarie table' stickyHeader sx={{ minWidth: '800px' }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} id={column.key} className='results-table-header'>
                  {formatMessage(column.label)}
                </TableCell>
              ))}
              <TableCell className='results-table-header actions-header' sx={{ width: '90px' }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {accounts.length > 0 ? (
              accounts.map((account) => <FiduciarieResultsRow key={account.username} account={account} columns={columns} onDelete={onDelete} onOpen={onOpen} />)
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align='center' sx={{ py: 3 }}>
                  {formatMessage(fiduciarieMessages.noResults)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {(pagination?.pagineTotali ?? 0) > 1 && (
        <div className='paginator'>
          <PageSelector pagination={pageSelectorPagination} showPreviousPage={showPreviousPage} showNextPage={showNextPage} showPage={showPage} />
        </div>
      )}
    </Fragment>
  );
};
